import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@contexts/AuthContext";

import api from "@helpers/api";
import { useWallets, WALLET_PROVIDERS } from "@helpers/wallets";

import { SPLIT_INVITE_STATE } from "../enums/splitInviteStates";
import {
  adaptSplitInvitation,
  mapSplitInviteError,
} from "../helpers/adaptSplitInvitation";

// Normalise the account email — AuthContext stores it as { address }, but
// tolerate a plain string too (mirrors useMentoringInvite).
function accountEmailOf(user) {
  const raw = user?.email?.address ?? user?.email ?? "";
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Private split-invite (§2). The route is authenticated, so the visitor is
 * always a real signed-in user (the SPA bounced them through auth.ginastee.com
 * if they were logged out). This owns the authenticated GET, derives a single
 * SPLIT_INVITE_STATE, layers the email gate (account-email === invited_email)
 * over the actions, and runs accept (wallet-gated) / decline.
 */
export default function useSplitInvite(token) {
  const navigate = useNavigate();
  const { t } = useTranslation("member");
  const { user, goToAuth } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  // A terminal state derived from the fetch (or an action) that supersedes the
  // invitation: accepted | declined | not_found | expired | used | error.
  const [terminal, setTerminal] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const requestRef = useRef(0);
  // One-shot guard so the awaiting_wallet auto-finalize fires at most once.
  const finalizeRef = useRef(false);

  // ── Fetch the invite (authenticated) ───────────────────────────────────────
  const load = useCallback(async () => {
    if (!token) {
      setTerminal(SPLIT_INVITE_STATE.NOT_FOUND);
      setLoading(false);
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setTerminal(null);
    setInvitation(null);

    const res = await api.get(`split/invite/${token}`);
    if (requestId !== requestRef.current) return;

    if (res?.response) {
      setTerminal(mapSplitInviteError(res.response));
    } else {
      const invite = adaptSplitInvitation(res?.data);
      setInvitation(invite);
      if (!invite) setTerminal(SPLIT_INVITE_STATE.NOT_FOUND);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const community = invitation?.community ?? null;
  const owner = invitation?.owner ?? null;
  const invitedEmail = (invitation?.invited_email ?? "").trim();
  const accountEmail = accountEmailOf(user);

  // ── Email gate (security boundary; the token alone can't accept) ───────────
  const emailMatches =
    !!accountEmail &&
    !!invitedEmail &&
    accountEmail.toLowerCase() === invitedEmail.toLowerCase();

  // ── Resolve the single render state ────────────────────────────────────────
  const state = useMemo(() => {
    if (loading) return SPLIT_INVITE_STATE.LOADING;
    if (terminal) return terminal;
    if (!invitation) return SPLIT_INVITE_STATE.ERROR;

    // Wrong account beats the invite body so we never reveal it to a stranger.
    if (!emailMatches) return SPLIT_INVITE_STATE.WRONG_EMAIL;

    const status = invitation.status;
    if (status === "declined") return SPLIT_INVITE_STATE.DECLINED;
    if (status === "expired") return SPLIT_INVITE_STATE.EXPIRED;
    if (status === "accepted" || status === "revoked") return SPLIT_INVITE_STATE.USED;

    // pending / awaiting_wallet are actionable — but an elapsed expiry wins even
    // if the GET was lenient enough to return 200.
    const expiresAt = invitation.expires_at;
    if (expiresAt && new Date(expiresAt).getTime() < Date.now())
      return SPLIT_INVITE_STATE.EXPIRED;

    return SPLIT_INVITE_STATE.VALID;
  }, [loading, terminal, invitation, emailMatches]);

  const awaitingWallet = invitation?.status === "awaiting_wallet";
  const canRespond = state === SPLIT_INVITE_STATE.VALID && emailMatches;
  const {
    hasAnyConnected,
    loading: walletsLoading,
  } = useWallets({ enabled: awaitingWallet && emailMatches });

  // Surface an action error: a 422 flips the page to its terminal state, a 403
  // explains the email mismatch, anything else is a generic toast.
  const handleActionError = useCallback((response) => {
    const status = response?.status;
    const message = response?.data?.message;
    if (status === 422) {
      setTerminal(mapSplitInviteError(response));
      window.setSnackbar?.(message || t("splitInvite.feedback.unavailable"));
    } else if (status === 403) {
      setTerminal(SPLIT_INVITE_STATE.WRONG_EMAIL);
    } else {
      window.setSnackbar?.(message || t("splitInvite.feedback.genericError"));
    }
  }, [t]);

  // ── Accept (wallet-gated) ──────────────────────────────────────────────────
  // `silent` skips the no-wallet redirect — used by the auto-finalize, which has
  // already confirmed an approved wallet, so it must never bounce to /banking.
  const accept = useCallback(
    async ({ silent = false } = {}) => {
      if (submitting) return;
      setSubmitting(true);

      const res = await api.post(`split/invite/${token}/accept`);

      if (res?.response) {
        handleActionError(res.response);
        setSubmitting(false);
        return;
      }

      if (res?.data?.requires_wallet) {
        if (silent) {
          // Wallet not approved yet — re-fetch so the card reflects awaiting_wallet
          // and let the user trigger the banking detour manually.
          setSubmitting(false);
          load();
          return;
        }
        navigate(`/banking?provider=${WALLET_PROVIDERS.STRIPE}&return=${encodeURIComponent(`/splitInvite/${token}`)}`);
        // No setSubmitting(false): the navigation unmounts this page.
        return;
      }

      window.setSnackbar?.(t("splitInvite.feedback.accepted"));
      setTerminal(SPLIT_INVITE_STATE.ACCEPTED);
      setSubmitting(false);
    },
    [submitting, token, navigate, handleActionError, load, t],
  );

  // ── Auto-finalize after the banking detour ─────────────────────────────────
  // On mount, if the invite is awaiting_wallet and the user now has an approved
  // wallet, complete the accept (the backend is idempotent).
  useEffect(() => {
    if (finalizeRef.current) return;
    if (!awaitingWallet || !emailMatches) return;
    if (walletsLoading || !hasAnyConnected) return;
    finalizeRef.current = true;
    accept({ silent: true });
  }, [awaitingWallet, emailMatches, walletsLoading, hasAnyConnected, accept]);

  // ── Decline (confirm) ──────────────────────────────────────────────────────
  const doDecline = useCallback(async () => {
    const res = await api.post(`split/invite/${token}/decline`);

    if (res?.response) {
      handleActionError(res.response);
      return;
    }

    window.setSnackbar?.(t("splitInvite.feedback.declined"));
    setTerminal(SPLIT_INVITE_STATE.DECLINED);
  }, [token, handleActionError, t]);

  const decline = useCallback(() => {
    if (!canRespond || submitting) return;
    window.openConfirm?.({
      title: t("splitInvite.confirmDecline.title"),
      headline: t("splitInvite.confirmDecline.headline"),
      subtitle: t("splitInvite.confirmDecline.subtitle"),
      buttons: [
        { text: t("splitInvite.confirmDecline.confirmButton"), variant: "contained", color: "error", onClick: doDecline },
        { text: t("common:actions.cancel"), variant: "text" },
      ],
    });
  }, [canRespond, submitting, doDecline, t]);

  // "Entrar com outra conta" — drop the wrong session, then bounce to auth (the
  // redirect_uri preserves this deep link, so they return here authenticated).
  const switchAccount = useCallback(() => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("@user");
    } catch (e) {
      /* ignore */
    }
    goToAuth?.();
  }, [goToAuth]);

  return {
    state,
    invitation,
    community,
    owner,
    invitedEmail,
    accountEmail,
    percentile: invitation?.percentile ?? null,
    awaitingWallet,
    emailMatches,
    canRespond,
    submitting,
    accept,
    decline,
    switchAccount,
  };
}
